import { ref } from 'vue'
import { db, auth } from '@/firebase/firebase'
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore'

// 피드백 카테고리
export const FEEDBACK_CATEGORIES = [
  { id: 'suggestion', label: '건의사항', icon: 'lightbulb', color: 'blue' },
  { id: 'bug', label: '오류/버그', icon: 'bug', color: 'red' },
  { id: 'report', label: '신고', icon: 'flag', color: 'orange' },
  { id: 'inquiry', label: '문의', icon: 'question', color: 'green' },
  { id: 'other', label: '기타', icon: 'dots', color: 'slate' }
]

// 상태
export const FEEDBACK_STATUS = {
  pending: { label: '접수됨', color: 'slate' },
  in_progress: { label: '처리중', color: 'blue' },
  resolved: { label: '처리완료', color: 'green' },
  rejected: { label: '반려', color: 'red' }
}

export function useFeedback() {
  const loading = ref(false)
  const error = ref(null)

  // 피드백 생성
  const createFeedback = async (data) => {
    loading.value = true
    error.value = null

    try {
      const user = auth.currentUser
      if (!user) throw new Error('로그인이 필요합니다')

      const feedbackData = {
        partnerId: user.uid,
        partnerEmail: user.email,
        category: data.category,
        title: data.title,
        content: data.content,
        status: 'pending',
        adminReply: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        resolvedAt: null
      }

      const docRef = await addDoc(collection(db, 'partnerFeedbacks'), feedbackData)

      return {
        id: docRef.id,
        ...feedbackData
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 내 피드백 목록 조회
  const getMyFeedbacks = async (options = {}) => {
    loading.value = true
    error.value = null

    try {
      const user = auth.currentUser
      if (!user) throw new Error('로그인이 필요합니다')

      const { category = null, status = null, pageSize = 20, lastDoc = null } = options

      let q = query(
        collection(db, 'partnerFeedbacks'),
        where('partnerId', '==', user.uid),
        orderBy('createdAt', 'desc')
      )

      if (category) {
        q = query(
          collection(db, 'partnerFeedbacks'),
          where('partnerId', '==', user.uid),
          where('category', '==', category),
          orderBy('createdAt', 'desc')
        )
      }

      if (status) {
        q = query(
          collection(db, 'partnerFeedbacks'),
          where('partnerId', '==', user.uid),
          where('status', '==', status),
          orderBy('createdAt', 'desc')
        )
      }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc), limit(pageSize))
      } else {
        q = query(q, limit(pageSize))
      }

      const snapshot = await getDocs(q)
      const feedbacks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return {
        feedbacks,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === pageSize
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 피드백 상세 조회
  const getFeedback = async (feedbackId) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'partnerFeedbacks', feedbackId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('피드백을 찾을 수 없습니다')
      }

      return {
        id: docSnap.id,
        ...docSnap.data()
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 전체 피드백 목록 (Admin용)
  const getAllFeedbacks = async (options = {}) => {
    loading.value = true
    error.value = null

    try {
      const { category = null, status = null, pageSize = 50, lastDoc = null } = options

      let q = query(
        collection(db, 'partnerFeedbacks'),
        orderBy('createdAt', 'desc')
      )

      if (category) {
        q = query(
          collection(db, 'partnerFeedbacks'),
          where('category', '==', category),
          orderBy('createdAt', 'desc')
        )
      }

      if (status) {
        q = query(
          collection(db, 'partnerFeedbacks'),
          where('status', '==', status),
          orderBy('createdAt', 'desc')
        )
      }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc), limit(pageSize))
      } else {
        q = query(q, limit(pageSize))
      }

      const snapshot = await getDocs(q)
      const feedbacks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return {
        feedbacks,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === pageSize
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 피드백 상태 업데이트 (Admin용)
  const updateFeedbackStatus = async (feedbackId, newStatus, adminReply = null) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'partnerFeedbacks', feedbackId)

      const updateData = {
        status: newStatus,
        updatedAt: new Date().toISOString()
      }

      if (adminReply !== null) {
        updateData.adminReply = adminReply
      }

      if (newStatus === 'resolved' || newStatus === 'rejected') {
        updateData.resolvedAt = new Date().toISOString()
      }

      await updateDoc(docRef, updateData)

      return true
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 미처리 피드백 개수 (Admin 대시보드용)
  const getPendingCount = async () => {
    try {
      const q = query(
        collection(db, 'partnerFeedbacks'),
        where('status', '==', 'pending')
      )
      const snapshot = await getDocs(q)
      return snapshot.size
    } catch (err) {
      console.error('Error getting pending count:', err)
      return 0
    }
  }

  // 시간 포맷
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date

    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return '방금 전'
    if (minutes < 60) return `${minutes}분 전`
    if (hours < 24) return `${hours}시간 전`
    if (days < 7) return `${days}일 전`

    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return {
    loading,
    error,
    createFeedback,
    getMyFeedbacks,
    getFeedback,
    getAllFeedbacks,
    updateFeedbackStatus,
    getPendingCount,
    formatTimeAgo
  }
}
